import { ModalProps } from "antd/lib/modal";
import { ShowModalFunction } from "Components/General/Modal/ModalContext";
import { I18N_NS } from "Config/i18n";
import { observer } from "mobx-react";
import { CopyDirectoryModal } from "Pages/Project/Documents/Components/CopyDirectoryModal";
import { withTranslation, WithTranslation } from "react-i18next";
import { projectsDataStore, userInfoStore } from "Stores";
import { AccessRightsMatrix } from "../AccessRightsMatrix/AccessRightsMatrix";
import { LabelCalculator } from "../Calculator/LabelCalculator";
import { documentsTableStore } from "../Stores/DocumentsTableStore";
import { AccessRights } from "./AccessRights";
import { AddFolderMetadata } from "./AddFolderMetadata";
import { CreateLinkFolderModal } from "./CreateLinkFolder";
import { DocumentsSitemap } from "./DocumentsSitemap";
import { FolderNameForm, FolderNameFormProps } from "./FolderNameForm";
import { MoveFolderForm, MoveFolderFormProps } from "./MoveFolderForm";
import { ActionButtonProps, MultiActionsMenu } from "./MultiActionsMenu";
import { modal } from "Components/General/Modal/AntdModal";
import { FolderMetadataOverviewPage } from "../Pages/FolderMetadataOverviewPage";

interface ToolsMenuProps extends WithTranslation {
  showModal?: ShowModalFunction;
  folderActions: API_FolderActions;
  folderMenuWorkflows: API_FolderDataEntity["folderMenueWorkflows"];
  folderId: number;
}

export const ToolsMenu = withTranslation(I18N_NS.PROJECT)(
  observer(({ showModal, t, folderActions = {}, folderMenuWorkflows = [], folderId }: ToolsMenuProps) => {
    const actionsConfig: ActionButtonProps[] = [];
    const modalBaseProps: ModalProps = { footer: null };

    const handleDeleteFolderClick = async () => {
      try {
        const folderInfo = await documentsTableStore.getActiveItemsCountForDelete(
          projectsDataStore.projectId,
          folderId
        );
        const folderCount = folderInfo.numberActiveFolders;
        const fileCount = folderInfo.numberActiveFiles;
        const linkCount = folderInfo.numberLinks;

        modal.confirm({
          title: t("Mark as deleted"),
          content:
            folderCount || fileCount || linkCount ? (
              <>
                <p>
                  {t(
                    // eslint-disable-next-line max-len
                    "The folder contains {{folderCount}} subfolders, {{fileCount}} files, and {{linkCount}} links which will be deleted as well. This action cannot be undone. Files and folders must be restored individually, links must be created anew.",
                    { folderCount: folderCount, fileCount: fileCount, linkCount: linkCount }
                  )}
                </p>
                <p>{t("Do you want to proceed?")}</p>
              </>
            ) : (
              t("Do you really want to mark current folder as deleted?")
            ),
          okText: t("Yes"),
          cancelText: t("No"),
          zIndex: 9999,
          onOk: () => documentsTableStore.deleteFolder(folderId),
        });
      } catch (error) {
        throw error;
      }
    };

    if (folderActions.createFolder) {
      actionsConfig.push({
        icon: "folder-plus",
        label: t("New directory"),
        onClick: () =>
          showModal(FolderNameForm, {
            modalProps: { ...modalBaseProps, title: t("New directory") },
          }),
      });
    }
    if (folderActions.createSymlink) {
      actionsConfig.push({
        icon: "link",
        label: t("Create link"),
        onClick: () =>
          showModal(CreateLinkFolderModal, {
            modalProps: { ...modalBaseProps, title: t("Create link") },
            componentProps: {
              sourceFolderId: folderId,
              initialFolderName: documentsTableStore.currentFolder.name,
              createSymlinkFolder: documentsTableStore.createSymlinkFolder,
            },
          }),
      });
    }
    if (folderActions.rename) {
      actionsConfig.push({
        icon: "edit",
        label: t("Edit directory"),
        onClick: () =>
          showModal<Pick<FolderNameFormProps, "isEdit" | "initialName">>(FolderNameForm, {
            modalProps: { ...modalBaseProps, title: t("Edit directory") },
            componentProps: {
              initialName: documentsTableStore.currentFolder
                ? documentsTableStore.currentFolder.originalname || documentsTableStore.currentFolder.name
                : undefined,
              isEdit: true,
            },
          }),
      });
    }
    if (folderActions.move) {
      actionsConfig.push({
        icon: "dolly",
        label: t("Move directory"),
        onClick: () =>
          showModal<Pick<MoveFolderFormProps, "folderId">>(MoveFolderForm, {
            modalProps: { ...modalBaseProps, title: t("Move directory") },
            componentProps: {
              folderId: documentsTableStore.currentFolderId,
            },
          }),
      });
    }
    if (folderActions.delete) {
      actionsConfig.push({
        icon: "trash-alt",
        label: t("Delete directory"),
        onClick: handleDeleteFolderClick,
      });
    }
    if (folderActions.restoreFolder) {
      actionsConfig.push({
        icon: "trash-restore-alt",
        label: t("Restore"),
        onClick: () =>
          modal.confirm({
            title: t("Restore folder"),
            content: t("Do you really want to restore the folder?"),
            okText: t("Yes"),
            cancelText: t("No"),
            onOk: () => documentsTableStore.restoreFolder(folderId),
          }),
      });
    }
    if (folderActions.rightsManagement) {
      actionsConfig.push({
        icon: "key",
        label: t("Rights"),
        onClick: () =>
          showModal(AccessRights, {
            modalProps: { ...modalBaseProps, width: "100%", style: { maxWidth: 1200 }, title: t("Rights") },
            componentProps: {
              folderId: documentsTableStore.currentFolderId,
              isReadAccess: documentsTableStore.currentFolder.folderActions.readAccessRights,
            },
          }),
      });
    }
    if (projectsDataStore.isAdmin) {
      actionsConfig.push({
        icon: "location",
        label: "Sitemap",
        onClick: () =>
          showModal(DocumentsSitemap, {
            modalProps: { ...modalBaseProps, title: "Sitemap" },
          }),
      });
    }
    if (userInfoStore.isSysAdmin || projectsDataStore.isAdmin) {
      actionsConfig.push({
        icon: "list",
        label: t("Folder metadata overview"),
        dataTestId: "documents-tools-folder-metadata-overview",
        onClick: () =>
          showModal(FolderMetadataOverviewPage, {
            modalProps: {
              className: "viewer-modal",
              width: "100%",
              style: { maxWidth: `calc(100vw - 40px)` },
              footer: null,
              destroyOnHidden: true,
              maskClosable: false,
              keyboard: false,
              closable: false,
            },
          }),
      });
    }
    if ((userInfoStore.isSysAdmin && projectsDataStore.currentProject.directoryRights) || folderActions.rightsMatrix) {
      actionsConfig.push({
        icon: "table",
        label: t("Access right matrix"),
        onClick: () =>
          showModal(AccessRightsMatrix, {
            modalProps: {
              className: "viewer-modal",
              width: "100%",
              style: { maxWidth: `calc(100vw - 40px)` },
              footer: null,
              destroyOnHidden: true,
              maskClosable: false,
              keyboard: false,
              closable: false,
            },
            componentProps: { folderId: documentsTableStore.currentFolderId },
          }),
      });
    }

    if (
      folderActions.assignLabelRule &&
      projectsDataStore.functions?.includes("FILE_LABELS") &&
      projectsDataStore.labelsSettings?.folderLabels
    ) {
      actionsConfig.push({
        icon: "calculator",
        label: t("Label calculator"),
        onClick: () =>
          showModal(LabelCalculator, {
            modalProps: {
              title: " ",
              width: "100%",
              style: { maxWidth: 1200 },
              styles: { body: { padding: "0 20px 20px" } },
              footer: null,
            },
          }),
      });
    }

    if (folderActions.assignMetadata) {
      actionsConfig.push({
        icon: "file-code",
        label: t("Add metadata"),
        onClick: () =>
          showModal(AddFolderMetadata, {
            modalProps: { ...modalBaseProps, width: "100%", style: { maxWidth: 1200 }, title: t("Add metadata") },
            componentProps: { folderId },
          }),
      });
    }
    if (folderActions.copyDirectory) {
      actionsConfig.push({
        icon: "copy",
        label: t("Copy directory"),
        onClick: () =>
          showModal(CopyDirectoryModal, {
            modalProps: { ...modalBaseProps, title: t("Copy directory") },
            componentProps: {
              sourceId: folderId,
              initialFolderName: documentsTableStore.currentFolder.name,
              canCopyMetadata: documentsTableStore.currentFolder.folderActions.assignMetadata,
              copyDirectory: documentsTableStore.copyDirectory,
              canCopyAccessRights: folderActions.copyDirectoryWithRights,
            },
          }),
      });
    }

    if (folderMenuWorkflows.length) {
      actionsConfig.push(
        ...folderMenuWorkflows.map((workflow) => ({
          icon: "code-branch" as const,
          label: workflow.name,
          onClick: () => documentsTableStore.startWorkflowForCurrentFolder(workflow),
        }))
      );
    }

    return actionsConfig.length ? (
      <div style={{ marginLeft: 10 }}>
        <MultiActionsMenu actionsConfig={actionsConfig} triggerDataTestId="documents-tools-menu" />
      </div>
    ) : null;
  })
);
